import React from "react";
import about_img_1 from "../assets/about_img_1.png";
import { useI18n } from "../i18n/I18nProvider";

export default function About() {
  const { t } = useI18n();

  return (
    <section
      id="about"
      className="relative bg-[#FFFDF8] pt-8 pb-4 md:pt-12 md:pb-6 px-6 lg:px-10"
    >
      <div className="max-w-6xl lg:max-w-7xl mx-auto">
        {/* ========================= TITLE ========================= */}
        <div className="text-center">
          <h2 className="text-3xl sm:text-[34px] md:text-[44px] lg:text-[54px] font-light text-ekatraBlue leading-tight">
            {t("about.heading.prefix")}{" "}
            <span className="font-extrabold text-ekatraBlue relative inline-block">
              {t("about.heading.highlight")}
              {/* underline + strokes stay same */}
              <span className="absolute left-0 right-0 mx-auto bottom-[-18px] flex justify-center">
                <svg
                  width="518"
                  height="33"
                  viewBox="0 0 518 33"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.00058 23.4229L75.6207 14.0957C98.9215 11.0182 122.614 13.1072 145.017 20.2143C170.278 28.228 197.132 29.8477 223.173 24.9281L289.347 12.4266C350.382 0.896156 413.197 2.81151 473.415 18.0392L512.501 27.9229"
                    stroke="#F1AF38"
                    strokeWidth="10"
                    strokeLinecap="round"
                  />
                </svg>

                <span className="absolute right-[-70px] top-[-90px]">
                  <svg
                    width="74"
                    height="68"
                    viewBox="0 0 74 68"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.1737 11.5L5.00122 42M68.1737 5L21.6737 49.5M37.6737 62.5L68.1737 49.5"
                      stroke="#F1AF38"
                      strokeWidth="10"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </span>
            </span>
          </h2>

          {/* PARAGRAPH */}
          <p className="mt-4 sm:mt-6 md:mt-8 lg:mt-10 text-base sm:text-lg md:text-xl text-[#033049] leading-relaxed sm:leading-[1.8] text-left sm:text-justify">
            {t("about.description")}
          </p>
        </div>

        {/* ========================= QUOTE + IMAGE ========================= */}
        <div className="mt-8 sm:mt-10 md:mt-12 lg:mt-14">
          <div className="grid grid-cols-1 md:grid-cols-[48%_48%] items-stretch gap-y-10 md:gap-y-0 md:gap-x-[4%]">
            {/* LEFT QUOTE SECTION */}
            <div className="relative flex flex-col items-start w-full">
              {/* TOP QUOTE SVG */}
              <div className="mb-4">
                <svg
                  width="71"
                  height="53"
                  viewBox="0 0 71 53"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M60.0817 40.2654C60.0817 43.7422 58.909 46.7338 56.5636 49.2403C54.1373 51.7468 51.3066 53 48.0716 53C44.7556 53 41.8441 51.6659 39.3369 48.9977C36.7489 46.3295 35.4549 42.9741 35.4549 38.9313C35.4549 31.7353 38.2855 24.4584 43.9469 17.1007C49.6082 9.74294 57.8172 4.04271 68.5737 0L71 3.881C64.9343 6.5492 60.0008 10.0259 56.1996 14.3112C53.2881 17.5454 51.8323 20.6178 51.8323 23.5286C51.8323 25.0648 52.0749 26.4394 52.5602 27.6522C52.8837 28.299 54.016 29.5927 55.957 31.5332C57.898 33.4737 58.9899 34.8078 59.2325 35.5355C59.7986 36.8291 60.0817 38.4058 60.0817 40.2654Z"
                    fill="#F1AF38"
                  />
                  <path
                    d="M24.6268 40.2654C24.6268 43.7422 23.4541 46.7338 21.1087 49.2403C18.6824 51.7468 15.8518 53 12.6167 53C9.30077 53 6.38923 51.6659 3.88206 48.9977C1.29402 46.3295 0 42.9741 0 38.9313C0 31.7353 2.83067 24.4584 8.49201 17.1007C14.1534 9.74294 22.3623 4.04271 33.1188 0L35.5451 3.881C29.4794 6.5492 24.546 10.0259 20.7448 14.3112C17.8332 17.5454 16.3774 20.6178 16.3774 23.5286C16.3774 25.0648 16.6201 26.4394 17.1053 27.6522C17.4288 28.299 18.5611 29.5927 20.5021 31.5332C22.4432 33.4737 23.535 34.8078 23.7776 35.5355C24.3438 36.8291 24.6268 38.4058 24.6268 40.2654Z"
                    fill="#004668"
                  />
                </svg>
              </div>

              {/* CENTER TEXT */}
              <p
                className="font-playfair text-2xl sm:text-[28px] md:text-[32px] lg:text-[36px] font-semibold text-[#033049] italic leading-tight w-full"
                style={{
                  lineHeight: "1.15",
                  textShadow: "2px 2px 8px rgba(0,0,0,0.18)",
                  paddingLeft: "64px",
                }}
              >
                {t("about.quote")}
              </p>

              {/* BOTTOM QUOTE SVG */}
              <div className="mt-4 w-full flex justify-end">
                <svg
                  width="71"
                  height="53"
                  viewBox="0 0 71 54"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.9183 12.9748C10.9183 9.4325 12.091 6.38444 14.4364 3.83066C16.8627 1.27689 19.6934 0 22.9284 0C26.2444 0 29.1559 1.35927 31.6631 4.0778C34.2511 6.79634 35.5451 10.2151 35.5451 14.3341C35.5451 21.6659 32.7145 29.0801 27.0531 36.5767C21.3918 44.0732 13.1828 49.881 2.42629 54L0 50.0458C6.06572 47.3272 10.9992 43.7849 14.8004 39.4188C17.7119 36.1236 19.1677 32.9931 19.1677 30.0275C19.1677 28.4622 18.9251 27.0618 18.4398 25.8261C18.1163 25.167 16.984 23.849 15.043 21.8719C13.102 19.8947 12.0101 18.5355 11.7675 17.7941C11.2014 16.476 10.9183 14.8696 10.9183 12.9748Z"
                    fill="#F1AF38"
                  />
                  <path
                    d="M46.3732 12.9748C46.3732 9.4325 47.5459 6.38444 49.8913 3.83066C52.3176 1.27689 55.1482 0 58.3833 0C61.6992 0 64.6108 1.35927 67.1179 4.0778C69.706 6.79634 71 10.2151 71 14.3341C71 21.6659 68.1693 29.0801 62.508 36.5767C56.8466 44.0732 48.6377 49.881 37.8812 54L35.4549 50.0458C41.5206 47.3272 46.454 43.7849 50.2552 39.4188C53.1668 36.1236 54.6225 32.9931 54.6225 30.0275C54.6225 28.4622 54.3799 27.0618 53.8947 25.8261C53.5712 25.167 52.4389 23.849 50.4979 21.8719C48.5568 19.8947 47.465 18.5355 47.2224 17.7941C46.6562 16.476 46.3732 14.8696 46.3732 12.9748Z"
                    fill="#004668"
                  />
                </svg>
              </div>
            </div>

            {/* IMAGE */}
            <div className="flex justify-center md:justify-start">
              <img
                src={about_img_1}
                alt={t("about.imageAlt")}
                className="w-full h-full max-h-[420px] object-cover rounded-3xl shadow-lg border border-[#EFE7D9]"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
